import type { JSX } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LoginScreen, type LoginResult, type LoginScreenEndpoints } from '@cca/ui';

// The Vetting Portal adopts the estate's ONE sign-in screen (ADR-018 / AUTH-8):
// the shared @cca/ui <LoginScreen> replaces the bespoke LoginPage + VerifyPage
// clones — the LAST door in the sign-in-kit cutover, and the highest-risk one
// (vetting.confessinganglicans.nz is the live NZ police-vetting portal, and
// non-registrar approvers depend SOLELY on this OTP door — passkey enrol is
// registrar-only). Emerald #257E47 is the Vetting Portal's signature colour
// (UX-1) — it drives the whole screen, and it is the SAME emerald the worker's
// login-code email carries (renderOtpCodeEmail's accentColor), so screen and
// email are two faces of one door. Cookie session model: the worker sets an
// HttpOnly `vetting_session` cookie, so onSignedIn persists nothing auth-bearing
// — it just keeps the display hint the old VerifyPage kept (auth_email) and
// enters the app. The design-system classes (@cca/ui/components) + tokens
// (@cca/ui/tokens) are loaded globally in main.tsx, so nothing extra is here.

/** Vetting's same-origin worker paths. The passkey bases are UNCHANGED; OTP now
 *  mounts the shared createOtpSignInRoutes factory, so its two paths are
 *  /otp/request + /otp/verify (were /otp-request + /otp-verify). */
const VETTING_ENDPOINTS: LoginScreenEndpoints = {
  discoverOptions: '/api/vetting/passkeys/discover-options',
  discoverVerify: '/api/vetting/passkeys/discover-verify',
  passkeyOptions: '/api/vetting/passkeys/options',
  passkeyVerify: '/api/vetting/passkeys/verify',
  otpRequest: '/api/vetting/auth/otp/request',
  otpVerify: '/api/vetting/auth/otp/verify',
};

/** The unauthenticated sign-in door. Mounted at /auth/login; the shared screen
 *  owns BOTH the email step and the code step internally, so vetting no longer
 *  needs a separate routed /auth/verify. */
export function VettingSignIn(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo') || '/';

  const onSignedIn = (result: LoginResult): void => {
    // The session cookie is set by the worker (HttpOnly); keep only the display
    // hint the old VerifyPage persisted (auth_email — display only, never auth),
    // then enter the app.
    if (result.email) localStorage.setItem('auth_email', result.email);
    navigate(returnTo, { replace: true });
  };

  return (
    <LoginScreen
      portalName="Vetting Portal"
      accentColor="#257E47"
      endpoints={VETTING_ENDPOINTS}
      onSignedIn={onSignedIn}
      authzDeniedCopy="You are not registered as a vetting approver for any parish. Contact registrar@confessinganglicans.nz if you believe this is an error."
    />
  );
}
